import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { List } from 'immutable';
import { Subject } from 'rxjs';
import { LocalStorageService } from '../../../browser-manager-data/local-storage.service';
import { DialogService } from '../../../dialog/dialog.service';
import {
  CONVERT_TO_INVOICE_DONT_ASK_ME_AGAIN_KEY,
  DocumentActionsEnum,
  DocumentTypesEnum,
  ICopyDocumentOptions,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { Settings } from '../../../settings-data/settings.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { DropdownHorizontalAlignmentsEnum } from '../../../shared/components/dropdown/dropdown.component';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { XeroAccount } from '../../../xero-data/xero-account.model';
import { DIALOG_TYPE as convertToInvoiceDialog } from '../convert-to-invoice-dialog/convert-to-invoice-dialog.component';
import { DIALOG_TYPE as copyDocumentDialog } from '../copy-document-dialog/copy-document-dialog.data';
import { DocumentActionsBuilder } from './document-actions.builder';
import { DocumentDataActionBuilder } from '../../../document-data/document-data-action.builder';

export interface IDocumentAction {
  action: DocumentActionsEnum;
  payload?: any;
}

// TODO [refactor]: move actions and builder to parent, similar to QuickActions
@Component({
  selector: 'app-document-actions',
  templateUrl: './document-actions.component.html',
  styleUrls: ['./document-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentActionsBuilder],
})
export class DocumentActionsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() document: Document;
  @Input() savingDocument: boolean;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  @Input() saveAction: ITrackingAction;
  @Input() featureName: string;
  @Input() featureToggles: Toggles;
  @Input() taxes: List<Tax>;
  @Input() settings: Settings;
  @Input() xeroAccount: XeroAccount;

  @Output()
  onDocumentAction: EventEmitter<IDocumentAction> = new EventEmitter<IDocumentAction>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  dropdownHorizontalAlignment: DropdownHorizontalAlignmentsEnum;
  triggerIcon: IIcon;
  documentActions: List<List<DocumentActionsEnum>>;
  DocumentActionsEnum: typeof DocumentActionsEnum;
  disableCopyDocumentTypes: List<DocumentTypesEnum>;
  copyAction: ITrackingAction;
  convertAction: ITrackingAction;
  printAction: ITrackingAction;
  approveAction: ITrackingAction;
  declineAction: ITrackingAction;
  disableConvertAction: boolean;
  exportAction: ITrackingAction;

  constructor(
    private _dialogService: DialogService,
    private _documentActionsBuilder: DocumentActionsBuilder,
    private _utilsService: UtilsService,
    private _localStorageService: LocalStorageService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _documentDataActionBuilder: DocumentDataActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.document && this.document) {
      this.documentActions = this._documentActionsBuilder.buildDropdown(
        this.document
      );

      if (this.document.documentType === DocumentTypesEnum.estimate) {
        this.disableCopyDocumentTypes = List(
          this._utilsService.getConvertedDoc(this.document)
            ? [DocumentTypesEnum.invoice]
            : []
        );
        this.disableConvertAction = this.disableCopyDocumentTypes.some(
          (docType: DocumentTypesEnum) => docType === DocumentTypesEnum.invoice
        );
      }
    }

    if (changes.trackingScreenName && this.trackingScreenName) {
      this.copyAction = this._trackingActionBuilder.buildButtonTapped(
        'copy_document',
        this.trackingScreenName
      );
      this.convertAction = this._trackingActionBuilder.buildButtonTapped(
        'convert_to_invoice',
        this.trackingScreenName
      );
      this.printAction = this._trackingActionBuilder.buildButtonTapped(
        'print_document',
        this.trackingScreenName
      );
      this.approveAction = this._trackingActionBuilder.buildButtonTapped(
        'approve',
        this.trackingScreenName
      );
      this.declineAction = this._trackingActionBuilder.buildButtonTapped(
        'decline',
        this.trackingScreenName
      );
      this.exportAction = this._trackingActionBuilder.buildButtonTapped(
        'xero_manual_sync',
        this.trackingScreenName
      );
    }
  }

  ngOnInit() {
    this.dropdownHorizontalAlignment = DropdownHorizontalAlignmentsEnum.right;
    this.triggerIcon = {
      name: 'more-menu',
      size: IconSizesEnum.medium,
    };
    this.DocumentActionsEnum = DocumentActionsEnum;
  }

  saveDocument() {
    const documentAction: IDocumentAction = {
      action: DocumentActionsEnum.save_document,
    };

    this.onDocumentAction.emit(documentAction);
  }

  onConvertToInvoice() {
    if (
      this._localStorageService.getItem(
        CONVERT_TO_INVOICE_DONT_ASK_ME_AGAIN_KEY
      )
    ) {
      this.convertToInvoice();
    } else {
      this._dialogService.openDialog({ type: convertToInvoiceDialog });
    }
  }

  openCopyDocumentDialog() {
    this._dialogService.openDialog({ type: copyDocumentDialog });
  }

  onCopyDocument(copyDocumentOptions: ICopyDocumentOptions) {
    const documentAction: IDocumentAction = {
      action: DocumentActionsEnum.copy_document,
      payload: {
        ...copyDocumentOptions,
        saveEditing: true,
      },
    };

    this.onDocumentAction.emit(documentAction);
  }

  printDocument() {
    const documentAction: IDocumentAction = {
      action: DocumentActionsEnum.print_document,
      payload: this.document,
    };

    this.onDocumentAction.emit(documentAction);
  }

  approveDocument() {
    const documentAction: IDocumentAction = {
      action: DocumentActionsEnum.approve_document,
    };

    this.onDocumentAction.emit(documentAction);
  }

  declineDocument() {
    const documentAction: IDocumentAction = {
      action: DocumentActionsEnum.decline_document,
    };

    this.onDocumentAction.emit(documentAction);
  }

  convertToInvoice() {
    const copyDocumentOptions = this._documentDataActionBuilder.buildConvertToInvoiceOptions(
      this.taxes,
      this.document
    );
    const documentAction: IDocumentAction = {
      action: DocumentActionsEnum.convert_document_to_invoice,
      payload: {
        ...copyDocumentOptions,
        saveEditing: true,
      },
    };

    this.onDocumentAction.emit(documentAction);
  }

  ngOnDestroy() {
    this._dialogService.closeDialog({ type: convertToInvoiceDialog });
    this._dialogService.closeDialog({ type: copyDocumentDialog });

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
